import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { PreviewStateConfig } from 'src/utils/preview/types';

/**
 * X/Y coordinates for positioning elements
 */
export interface Coordinates {
  x: number;
  y: number;
}

/**
 * Types of UI controls for preview configuration
 * - DROPDOWN: Dropdown selector (e.g., tablet size)
 * - BUTTON_GROUP: Button group selector (e.g., orientation)
 */
export enum PreviewControlType {
  DROPDOWN = 'DROPDOWN',
  BUTTON_GROUP = 'BUTTON_GROUP',
}

/**
 * Option for a preview control
 */
export interface PreviewControlOption {
  label: string;
  value: string;
}

/**
 * Configuration for a single preview control (dropdown, button group, etc.)
 */
export interface PreviewControlConfig {
  type: PreviewControlType;
  key: PreviewControlKey;
  label: string;
  options:
    | PreviewControlOption[]
    | ((values: Record<PreviewControlKey, PreviewControlValue>) => PreviewControlOption[]);
  defaultValue: string;
  isVisible?: (values: Record<PreviewControlKey, PreviewControlValue>) => boolean;
}

/**
 * Strategy for generating preview images
 * - CURRENT_VARIANT: Generate preview only for currently selected variant
 */
export enum PreviewGenerationStrategy {
  CURRENT_VARIANT = 'CURRENT_VARIANT',
}

/**
 * Preview state keys for tablet configurations
 */
export enum FireTabletsPreviewState {
  TABLET_7INCH_PORTRAIT_FOS = '7Inch_Portrait_FOS',
  TABLET_7INCH_LANDSCAPE_FOS = '7Inch_Landscape_FOS',
  TABLET_8INCH_PORTRAIT_FOS = '8Inch_Portrait_FOS',
  TABLET_8INCH_LANDSCAPE_FOS = '8Inch_Landscape_FOS',
  TABLET_10INCH_PORTRAIT_FOS = '10Inch_Portrait_FOS',
  TABLET_10INCH_LANDSCAPE_FOS = '10Inch_Landscape_FOS',
  TABLET_11INCH_PORTRAIT_FOS = '11Inch_Portrait_FOS',
  TABLET_11INCH_LANDSCAPE_FOS = '11Inch_Landscape_FOS',
  TABLET_8INCH_PORTRAIT_KH = '8Inch_Portrait_KH',
  TABLET_8INCH_LANDSCAPE_KH = '8Inch_Landscape_KH',
  TABLET_11INCH_PORTRAIT_KH = '11Inch_Portrait_KH',
  TABLET_11INCH_LANDSCAPE_KH = '11Inch_Landscape_KH',
  TABLET_12INCH_PORTRAIT_KH = '12Inch_Portrait_KH',
  TABLET_12INCH_LANDSCAPE_KH = '12Inch_Landscape_KH',
  DEVICE_PREVIEW = 'TabletDevicePreview',
}

/**
 * Preview state keys for FireTV configurations
 */
export enum FireTVPreviewState {
  SELECTED = 'Selected',
  UNSELECTED = 'Unselected',
  VIDEO = 'Video',
  SCREEN_SAVER = 'ScreenSaver',
  DEVICE_PREVIEW = 'DevicePreview',
}

/**
 * Union type of all preview state enums
 */
export type PreviewState = FireTabletsPreviewState | FireTVPreviewState;
/**
 * Tablet size options
 */
export enum TabletSize {
  SEVEN_INCH = '7Inch',
  EIGHT_INCH = '8Inch',
  TEN_INCH = '10Inch',
  ELEVEN_INCH = '11Inch',
  TWELVE_INCH = '12Inch',
}

/**
 * Tablet orientation options
 */
export enum TabletOrientation {
  PORTRAIT = 'Portrait',
  LANDSCAPE = 'Landscape',
}

/**
 * Tablet device type options
 */
export enum TabletDeviceType {
  FOS = 'FOS',
  KH = 'KH',
}

/**
 * Tablet view mode — toggles between the static tablet preview canvas
 * and the live RDP-backed Device Preview tab.
 */
export enum TabletViewMode {
  TABLET_PREVIEW = 'TabletPreview',
  DEVICE_PREVIEW = 'DevicePreview',
}

/**
 * Control keys for tablet preview configuration
 */
export enum TabletControlKey {
  VIEW = 'view',
  SIZE = 'size',
  ORIENTATION = 'orientation',
  DEVICE_TYPE = 'deviceType',
}

/**
 * Control keys for FireTV preview configuration
 */
export enum FireTVControlKey {
  STATE = 'state',
}

/**
 * Union type of all preview control keys
 */
export type PreviewControlKey = TabletControlKey | FireTVControlKey;

/**
 * Union type of all preview control values
 */
export type PreviewControlValue =
  | TabletDeviceType
  | TabletSize
  | TabletOrientation
  | TabletViewMode
  | FireTVPreviewState;

/**
 * Behavior configuration for preview generation and state management
 * @property previewGeneration - Strategy for when to generate previews
 * @property stateResolution - Function to resolve state name from control values
 */
export interface PreviewBehaviorConfig {
  previewGeneration: PreviewGenerationStrategy;
  stateResolution: (values: Record<PreviewControlKey, PreviewControlValue>) => PreviewState;
}

/**
 * Configuration for download options
 * @property includeAssets - Include background/logo/video assets
 * @property includePreviews - Include generated preview images
 * @property previewStates - Optional filter for which previews to include (defaults to all)
 * @property supportedAssets - Optional array of supported asset types for this configuration
 */
export interface DownloadConfig {
  includeAssets: boolean;
  includePreviews: boolean;
  previewStates?: PreviewState[];
  supportedAssets?: AssetType[];
}

/**
 * Base configuration for preview rendering
 * Single source of truth for all preview-related configuration
 * @property controls - Array of UI controls for state/variant selection
 * @property behavior - Preview generation and state resolution behavior
 * @property defaultState - Default state/variant to display on mount
 * @property stateConfigs - Layer-based rendering configs for each state/variant
 * @property downloads - Optional download configuration (defaults to assets only)
 */
export interface PreviewConfigBase {
  controls: PreviewControlConfig[];
  behavior: PreviewBehaviorConfig;
  defaultState: PreviewState;
  stateConfigs: Partial<Record<PreviewState, PreviewStateConfig>>;
  downloads?: DownloadConfig;
}
